import * as React from "react";
import { Viewport, ViewSmall, ViewLarge } from "bernie-view";
import { formatText } from "bernie-l10n";
import { inject } from "mobx-react";

import { UitkPrimaryButton } from "uitk-react-button";
import { AnalyticsConfig } from "uitk-react-utils";

import GalleryCarousel from "components/shared/Gallery/GalleryCarousel/GalleryCarousel";
import GalleryMosaic from "components/shared/Gallery/GalleryMosaic/GalleryMosaic";
import { EntityType, HeartButton } from "components/shared/HeartButton/HeartButton";
import { getAttributionAltText } from "components/shared/AttributionText/AttributionText";
import { Sources } from "../DestinationHero";
import { AspectRatioEnum } from "stores/thumbor/ThumborStore";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedTrackEvent
} from "components/utility/analytics/FlexAnalyticsUtils";

import { DestinationHeroGalleryProps } from "../typings";
import { Media } from "typings/microserviceModels/destination-hero-flex-module";

enum RegionTypes {
  Neighborhood = "neighborhood"
}

export const DestinationHeroGallery = inject("thumbor", "analytics")(
  (props: DestinationHeroGalleryProps) => {
    const { flexComponent, context } = props;
    const { model } = flexComponent;
    const { content, flightsLink, hotelsLink } = model;

    const { title, subTitle } = content;
    const isMobile = context && context.deviceInformation && context.deviceInformation.mobile;

    /* covered in e2e test */
    /* istanbul ignore next line */
    const primaryButtonAnalytics = (moduleName: string): AnalyticsConfig => {
      return {
        id: moduleName,
        callback: (theId, description) => {
          const flexTrackingInfo: FlexTrackingInfo = {
            moduleName: theId,
            action: Action.CLICK,
            linkName: description
          };
          sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
        }
      };
    };

    const pageHeading = (
      <>
        <span>{title}</span>

        {subTitle && (
          <span className="uitk-type-400 uitk-type-regular all-t-margin-one">{subTitle}</span>
        )}
      </>
    );

    const heartButton = () => {
      const { id, name, type } = context && context.searchContext && context.searchContext.location;
      const regionId = String(id);
      const regionType = String(type);

      if (!(name && regionId && regionType)) {
        return null;
      }

      const entityType =
        regionType === RegionTypes.Neighborhood ? EntityType.NEIGHBORHOOD : EntityType.CITY;

      return (
        <HeartButton
          moduleName="TG.LP.Dest.Hero"
          heartableEntity={{
            id: regionId,
            name,
            entityType,
            payload: { favoriteId: regionId }
          }}
        />
      );
    };

    const callToActionButtons = () => {
      if (!(hotelsLink && flightsLink)) {
        return null;
      }

      return (
        <div className="uitk-flex uitk-flex-align-items-flex-end uitk-flex-justify-content-flex-end">
          {hotelsLink && (
            <UitkPrimaryButton
              text={formatText("destinationHero.findHotels")}
              tag="a"
              href={hotelsLink}
              className="all-r-margin-two"
              analytics={primaryButtonAnalytics("TG.LP.Dest.Snapshot.Hotels")}
            />
          )}

          {flightsLink && (
            <UitkPrimaryButton
              text={formatText("destinationHero.findFlights")}
              tag="a"
              href={flightsLink}
              analytics={primaryButtonAnalytics("TG.LP.Dest.Snapshot.Flights")}
            />
          )}
        </div>
      );
    };

    const imageGallery = () => {
      const { media } = content;
      if (!(media && media.length)) {
        return null;
      }

      const { thumbor } = props;

      const [width, height] = isMobile ? [360, 224] : [1040, 580];

      const images = media.map((image: Media) => {
        const {
          mediaUrl,
          mediaUrlOriginal,
          mediaAlt,
          origin,
          source,
          author,
          authorUrl,
          license
        } = image;

        const thumborizedImageUrl = thumbor
          ? thumbor.safelyGenerateThumborizedImageUrl(
              mediaUrl,
              width,
              height,
              context,
              AspectRatioEnum.DEFAULT,
              isMobile,
              80
            )
          : mediaUrl;

        const isAttributionRequired = source === Sources.Trover || source === Sources.Wikimedia;

        return {
          alt: getAttributionAltText(
            source,
            origin,
            title,
            mediaAlt,
            author,
            license,
            mediaUrlOriginal
          ),
          src: thumborizedImageUrl,
          source,
          origin,
          caption: mediaAlt,
          thumbnailSrc: thumborizedImageUrl,
          attribUrl: mediaUrlOriginal,
          attribRequired: isAttributionRequired,
          authorName: author,
          authorUrl,
          licenseSuite: license && license.suite && license.suite,
          licenseName: license && license.name && license.name,
          licenseUrl: license && license.url && license.url,
          originalMediaUrl: mediaUrl
        };
      });

      return (
        <Viewport>
          <ViewSmall>
            <GalleryCarousel
              analyticsId="TG.LP"
              analyticsModule="Dest"
              analytics={props.analytics}
              title={formatText("destinationImageGallery.thumbnails.title", title)}
              images={images}
            />
          </ViewSmall>

          <ViewLarge>
            <GalleryMosaic
              analyticsId="TG.LP"
              analyticsModule="Dest"
              analytics={props.analytics}
              title={formatText("destinationImageGallery.title", title)}
              images={images}
            />
          </ViewLarge>
        </Viewport>
      );
    };

    return (
      <div
        id={flexComponent.id}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
        className="DestinationHeroGallery uitk-cell all-cell-1-1"
      >
        <meta itemProp="name" content={subTitle ? `${title}, ${subTitle}` : title} />
        <meta itemProp="image" content={content.media[0].mediaUrl} />

        <Viewport>
          <ViewSmall>
            <div className="DestinationHeroGalleryTitle uitk-flex uitk-flex-justify-content-space-between all-y-padding-four">
              <h1 className="uitk-type-display-800 uitk-flex uitk-flex-column">{pageHeading}</h1>

              {heartButton()}
            </div>
          </ViewSmall>

          <ViewLarge>
            <div className="SimpleContainer uitk-flex uitk-flex-justify-content-space-between all-y-padding-three">
              <div className="DestinationHeroGalleryTitle uitk-flex large-icon">
                <h1 className="uitk-type-display-900 uitk-flex uitk-flex-column all-b-margin-one">
                  {pageHeading}
                </h1>

                {heartButton()}
              </div>

              {callToActionButtons()}
            </div>
          </ViewLarge>
        </Viewport>

        {imageGallery()}
      </div>
    );
  }
);
